import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { fires } from "../../firebase";

const COLLECTION_NAME = "sns_emails";
const LOAD_LIMIT = 250;

const timestampFields = ["timestamp", "createdAt", "updatedAt", "sentAt"];

const statusVariantMap = {
  send: "primary",
  sent: "primary",
  delivery: "success",
  delivered: "success",
  bounce: "danger",
  bounced: "danger",
  complaint: "warning",
  reject: "danger",
  renderingfailure: "danger",
  deliverydelay: "warning",
  open: "info",
  opened: "info",
  click: "info",
  clicked: "info",
};

const preferredStatusOrder = ["open", "opened", "clicked", "click", "delivered", "delivery"];

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function formatDate(value) {
  const date = toDate(value);
  return date ? date.toLocaleString() : "N/A";
}

function getRecipient(row) {
  const destination = row.mail?.destination;
  if (Array.isArray(destination) && destination.length > 0) return String(destination[0]);
  return String(row.email || row.destination || "N/A");
}

function getSubject(row) {
  return String(row.mail?.commonHeaders?.subject || row.mail?.headers?.subject || row.subject || "N/A");
}

function getEventStatus(event) {
  const status = (
    event?.eventType ||
    event?.notificationType ||
    event?.status ||
    event?.type ||
    event?.delivery?.status ||
    "unknown"
  );

  if (normalizeStatus(status) === "click") return "clicked";
  if (normalizeStatus(status) === "delivery") return "delivered";
  return status;
}

function normalizeStatus(status) {
  return String(status || "unknown").replace(/\s+/g, "").toLowerCase();
}

function isOpenStatus(status) {
  const normalized = normalizeStatus(status);
  return normalized === "open" || normalized === "opened";
}

function getEventTime(event) {
  return (
    event?.receivedAt ||
    event?.timestamp ||
    event?.mail?.timestamp ||
    event?.delivery?.timestamp ||
    event?.createdAt
  );
}

function getLatestEvent(events) {
  if (!Array.isArray(events) || events.length === 0) return null;
  return [...events].sort((a, b) => {
    const bTime = toDate(getEventTime(b))?.getTime() || 0;
    const aTime = toDate(getEventTime(a))?.getTime() || 0;
    return bTime - aTime;
  })[0];
}

function getRowTime(row) {
  for (const field of timestampFields) {
    const date = toDate(row[field]);
    if (date) return date;
  }

  const latestEvent = getLatestEvent(row.events);
  return toDate(getEventTime(latestEvent));
}

function getReason(event) {
  return (
    event?.bounce?.bounceType ||
    event?.bounce?.bounceSubType ||
    event?.complaint?.complaintFeedbackType ||
    event?.delivery?.smtpResponse ||
    event?.reject?.reason ||
    event?.failure?.errorMessage ||
    event?.error ||
    ""
  );
}

function getOpenEvents(events) {
  return Array.isArray(events)
    ? events.filter((event) => isOpenStatus(getEventStatus(event)))
    : [];
}

function getPreferredEvent(events) {
  if (!Array.isArray(events) || events.length === 0) return null;

  for (const preferredStatus of preferredStatusOrder) {
    const matchingEvents = events.filter((event) => normalizeStatus(getEventStatus(event)) === preferredStatus);
    if (matchingEvents.length > 0) return getLatestEvent(matchingEvents);
  }

  return getLatestEvent(events);
}

function getGroupedEvents(row) {
  const records = Array.isArray(row?.records) ? row.records : [row];
  return records
    .flatMap((record) => {
      const events = Array.isArray(record?.events) ? record.events : [];
      return events.map((event) => ({
        event,
        recordId: record.id,
        messageId: event?.mail?.messageId || record.mail?.messageId || "N/A",
      }));
    })
    .sort((a, b) => {
      const bTime = toDate(getEventTime(b.event))?.getTime() || 0;
      const aTime = toDate(getEventTime(a.event))?.getTime() || 0;
      return bTime - aTime;
    });
}

function StatusBadge({ status }) {
  const normalized = normalizeStatus(status);
  return (
    <Badge bg={statusVariantMap[normalized] || "secondary"}>
      {status || "unknown"}
    </Badge>
  );
}

export default function AdminColdEmail() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedEmail, setSelectedEmail] = useState(null);

  const loadEmails = async () => {
    setLoading(true);
    setError("");

    try {
      let snapshot = null;
      let lastError = null;

      for (const field of timestampFields) {
        try {
          const fieldSnapshot = await fires
            .collection(COLLECTION_NAME)
            .orderBy(field, "desc")
            .limit(LOAD_LIMIT)
            .get();
          snapshot = fieldSnapshot;
          if (!fieldSnapshot.empty) break;
        } catch (err) {
          lastError = err;
        }
      }

      if (!snapshot || snapshot.empty) {
        try {
          snapshot = await fires.collection(COLLECTION_NAME).limit(LOAD_LIMIT).get();
        } catch (err) {
          throw lastError || err;
        }
      }

      const rows = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .sort((a, b) => {
          const bTime = getRowTime(b)?.getTime() || 0;
          const aTime = getRowTime(a)?.getTime() || 0;
          return bTime - aTime;
        });

      setEmails(rows);
    } catch (err) {
      console.error("Failed to load SNS emails:", err);
      setError("Failed to load cold email data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmails();
  }, []);

  const recordsWithStatus = useMemo(
    () =>
      emails.map((row) => {
        const events = Array.isArray(row.events) ? row.events : [];
        const latestEvent = getLatestEvent(events);
        const preferredEvent = getPreferredEvent(events);
        const openEvents = getOpenEvents(events);
        const latestOpenEvent = getLatestEvent(openEvents);
        return {
          ...row,
          recipient: getRecipient(row),
          subject: getSubject(row),
          latestStatus: getEventStatus(preferredEvent),
          latestAt: getEventTime(latestEvent) || getRowTime(row),
          reason: getReason(preferredEvent),
          eventCount: events.length,
          openCount: openEvents.length,
          latestOpenAt: getEventTime(latestOpenEvent),
          hasOpenEvent: openEvents.length > 0,
        };
      }),
    [emails]
  );

  const rowsWithStatus = useMemo(() => {
    const groupedRows = new Map();

    recordsWithStatus.forEach((row) => {
      const recipientKey = row.recipient.trim().toLowerCase();
      const key = recipientKey || row.id;
      const existingRow = groupedRows.get(key);
      const rowTime = toDate(row.latestAt)?.getTime() || 0;
      const existingTime = toDate(existingRow?.latestAt)?.getTime() || 0;

      if (!existingRow) {
        groupedRows.set(key, {
          ...row,
          ids: [row.id],
          records: [row],
          recordCount: 1,
          eventCount: row.eventCount,
        });
        return;
      }

      const nextRow = rowTime >= existingTime ? { ...row } : { ...existingRow };
      groupedRows.set(key, {
        ...nextRow,
        ids: [...existingRow.ids, row.id],
        records: [...existingRow.records, row],
        recordCount: existingRow.recordCount + 1,
        eventCount: existingRow.eventCount + row.eventCount,
      });
    });

    return Array.from(groupedRows.values())
      .map((row) => {
        const allEvents = row.records.flatMap((record) => (Array.isArray(record.events) ? record.events : []));
        const latestEvent = getLatestEvent(allEvents);
        const preferredEvent = getPreferredEvent(allEvents);
        const openEvents = getOpenEvents(allEvents);
        const latestOpenEvent = getLatestEvent(openEvents);

        return {
          ...row,
          latestStatus: getEventStatus(preferredEvent),
          latestAt: getEventTime(latestEvent) || row.latestAt,
          reason: getReason(preferredEvent),
          openCount: openEvents.length,
          latestOpenAt: getEventTime(latestOpenEvent),
          hasOpenEvent: openEvents.length > 0,
        };
      })
      .sort((a, b) => {
        const bTime = toDate(b.latestAt)?.getTime() || 0;
        const aTime = toDate(a.latestAt)?.getTime() || 0;
        return bTime - aTime;
      });
  }, [recordsWithStatus]);

  const statusOptions = useMemo(() => {
    const statuses = new Set();
    rowsWithStatus.forEach((row) => statuses.add(row.latestStatus));
    return Array.from(statuses).sort();
  }, [rowsWithStatus]);

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const startTime = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
    const endTime = endDate ? new Date(`${endDate}T23:59:59.999`).getTime() : null;

    return rowsWithStatus
      .filter((row) => {
        const latestTime = toDate(row.latestAt)?.getTime() || 0;
        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "open" && row.hasOpenEvent) ||
          normalizeStatus(row.latestStatus) === normalizeStatus(statusFilter);
        const matchesQuery =
          !normalizedQuery ||
          row.recipient.toLowerCase().includes(normalizedQuery) ||
          row.subject.toLowerCase().includes(normalizedQuery) ||
          row.ids.some((id) => id.toLowerCase().includes(normalizedQuery));
        const matchesStartDate = startTime === null || latestTime >= startTime;
        const matchesEndDate = endTime === null || latestTime <= endTime;

        return matchesStatus && matchesQuery && matchesStartDate && matchesEndDate;
      })
      .sort((a, b) => {
        const aTime = toDate(a.latestAt)?.getTime() || 0;
        const bTime = toDate(b.latestAt)?.getTime() || 0;
        return sortDirection === "asc" ? aTime - bTime : bTime - aTime;
      });
  }, [endDate, query, rowsWithStatus, sortDirection, startDate, statusFilter]);

  const stats = useMemo(() => {
    const counts = rowsWithStatus.reduce(
      (acc, row) => {
        const status = normalizeStatus(row.latestStatus);
        acc.total += 1;
        if (status.includes("deliver")) acc.delivered += 1;
        if (row.hasOpenEvent) acc.opened += 1;
        if (status.includes("bounce")) acc.bounced += 1;
        if (status.includes("complaint")) acc.complaints += 1;
        if (status.includes("reject") || status.includes("failure")) acc.failed += 1;
        return acc;
      },
      { total: 0, opened: 0, delivered: 0, bounced: 0, complaints: 0, failed: 0 }
    );
    counts.inProgress = counts.total - counts.delivered - counts.bounced - counts.complaints - counts.failed;
    return counts;
  }, [rowsWithStatus]);

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="text-center">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Loading cold email data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Card className="mb-4" style={{ border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
        <Card.Body>
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-1">
                <i className="fas fa-paper-plane me-2"></i>
                Admin Cold Email
              </h2>
              <p className="text-muted mb-0">BD email sending data from SNS events.</p>
            </Col>
            <Col xs="auto">
              <Button variant="outline-primary" onClick={loadEmails}>
                <i className="fas fa-sync-alt me-1"></i>
                Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col md={2}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted small">Unique Emails</div>
              <h3 className="mb-0">{stats.total}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted small">Opened</div>
              <h3 className="mb-0 text-info">{stats.opened}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted small">Delivered</div>
              <h3 className="mb-0 text-success">{stats.delivered}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted small">Bounced</div>
              <h3 className="mb-0 text-danger">{stats.bounced}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted small">Complaints</div>
              <h3 className="mb-0 text-warning">{stats.complaints}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted small">Failed</div>
              <h3 className="mb-0 text-danger">{stats.failed}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card style={{ border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
        <Card.Body>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Form.Control
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by email, subject, or document id"
              />
            </Col>
            <Col md={2}>
              <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="all">All statuses</option>
                <option value="open">Open events</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                aria-label="Start date"
              />
            </Col>
            <Col md={2}>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                aria-label="End date"
              />
            </Col>
            <Col md={2}>
              <Form.Select value={sortDirection} onChange={(event) => setSortDirection(event.target.value)}>
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </Form.Select>
            </Col>
          </Row>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="text-muted small">
              Showing {filteredRows.length} recipient{filteredRows.length === 1 ? "" : "s"}
              {statusFilter === "open" ? " with open events" : ""}
            </div>
            <Button
              variant={statusFilter === "open" ? "info" : "outline-info"}
              size="sm"
              onClick={() => setStatusFilter(statusFilter === "open" ? "all" : "open")}
            >
              <i className="fas fa-envelope-open me-1"></i>
              Opened Emails: {stats.opened}
            </Button>
          </div>

          {filteredRows.length === 0 ? (
            <Alert variant="info">No SNS email records found.</Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead>
                  <tr>
                    <th>Who</th>
                    <th>Latest Status</th>
                    <th>Opens</th>
                    <th>Latest Open</th>
                    <th>Sends</th>
                    <th>Events</th>
                    <th>Subject</th>
                    <th>Last Update</th>
                    <th>Reason</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className="fw-semibold">{row.recipient}</div>
                        <div className="text-muted small">{row.ids.slice(0, 2).join(", ")}</div>
                      </td>
                      <td>
                        <StatusBadge status={row.latestStatus} />
                      </td>
                      <td>{row.openCount || 0}</td>
                      <td>{formatDate(row.latestOpenAt)}</td>
                      <td>{row.recordCount}</td>
                      <td>{row.eventCount}</td>
                      <td style={{ minWidth: "220px" }}>{row.subject}</td>
                      <td>{formatDate(row.latestAt)}</td>
                      <td className="text-muted small" style={{ maxWidth: "280px" }}>
                        {row.reason || "N/A"}
                      </td>
                      <td className="text-end">
                        <Button variant="outline-secondary" size="sm" onClick={() => setSelectedEmail(row)}>
                          Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={Boolean(selectedEmail)} onHide={() => setSelectedEmail(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Email Event History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmail && (
            <>
              <div className="mb-3">
                <div className="fw-semibold">{selectedEmail.recipient}</div>
                <div className="text-muted small">{selectedEmail.subject}</div>
                <div className="text-muted small">
                  {selectedEmail.recordCount} send record{selectedEmail.recordCount === 1 ? "" : "s"}
                </div>
              </div>

              {selectedEmail.openCount > 0 && (
                <Card className="mb-3">
                  <Card.Body>
                    <h6 className="mb-3">Open Events</h6>
                    <Table bordered size="sm" responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Opened At</th>
                          <th>Message ID</th>
                          <th>Record ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getGroupedEvents(selectedEmail)
                          .filter(({ event }) => isOpenStatus(getEventStatus(event)))
                          .map(({ event, recordId, messageId }, index) => (
                            <tr key={`open-${recordId}-${index}`}>
                              <td>{formatDate(getEventTime(event))}</td>
                              <td>{messageId}</td>
                              <td>{recordId}</td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}

              {getGroupedEvents(selectedEmail).length > 0 ? (
                <Table bordered size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Timestamp</th>
                      <th>Message ID</th>
                      <th>Record ID</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getGroupedEvents(selectedEmail).map(({ event, recordId, messageId }, index) => (
                      <tr key={`${recordId}-${getEventStatus(event)}-${index}`}>
                        <td>
                          <StatusBadge status={getEventStatus(event)} />
                        </td>
                        <td>{formatDate(getEventTime(event))}</td>
                        <td>{messageId}</td>
                        <td>{recordId}</td>
                        <td className="text-muted small">{getReason(event) || "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="info">This record does not have any SNS events yet.</Alert>
              )}

              <details className="mt-3">
                <summary className="fw-semibold">Raw record</summary>
                <pre className="bg-light p-3 mt-2 small" style={{ maxHeight: "360px", overflow: "auto" }}>
                  {JSON.stringify(selectedEmail, null, 2)}
                </pre>
              </details>
            </>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}
